import React, { useEffect, useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { ChevronRight, ArrowLeft, CheckCircle2, Loader2, MessageCircle } from 'lucide-react';
// --- Background Elements ---
const FLOATING_FOODS = ['🍔', '🍕', '🍣', '☕', '🍟', '🌮', '🍰', '🥗', '🍩', '🥑'];
const BACKGROUND_WORDS = ['Pedidos', 'WhatsApp', 'Delivery', 'Marketplace', 'KDS', 'Tracking', 'Operación', 'Tecnología', 'Restaurantes', 'Crecimiento', 'Control', 'Inventario'];
// --- Form Types ---
type BusinessType = 'Restaurante' | 'Cafetería' | 'Pizzería' | 'Comida rápida' | 'Panadería' | 'Otro';
type MainGoal = 'Más pedidos' | 'Mejor control de cocina' | 'Menú digital' | 'Delivery / express' | 'Orden y operación' | 'Todo lo anterior';
interface FormData {
  owner_name: string;
  restaurant_name: string;
  whatsapp: string;
  social_handle: string;
  business_type: BusinessType | '';
  uses_whatsapp_orders: boolean | null;
  has_digital_menu: boolean | null;
  main_goal: MainGoal[];
}
export function OnboardingPage() {
  const [step, setStep] = useState(0); // 0 = Hero, 1-5 = Form, 6 = Success
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    owner_name: '',
    restaurant_name: '',
    whatsapp: '',
    social_handle: '',
    business_type: '',
    uses_whatsapp_orders: null,
    has_digital_menu: null,
    main_goal: []
  });
  // --- Handlers ---
  const handleNext = () => setStep(s => Math.min(s + 1, 6));
  const handleBack = () => setStep(s => Math.max(s - 1, 0));
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const cleanWhatsapp = formData.whatsapp.replace(/\D/g, '');
      if (!formData.owner_name || !formData.restaurant_name || !cleanWhatsapp || !formData.business_type) {
        throw new Error('Faltan campos obligatorios');
      }
      const {
        error
      } = await supabase.from('restaurant_applications').insert([{
        owner_name: formData.owner_name.trim(),
        restaurant_name: formData.restaurant_name.trim(),
        whatsapp: cleanWhatsapp,
        social_handle: formData.social_handle?.trim() || null,
        business_type: formData.business_type,
        uses_whatsapp_orders: formData.uses_whatsapp_orders ?? false,
        has_digital_menu: formData.has_digital_menu ?? false,
        main_goal: formData.main_goal.join(', ') || null,
        status: 'pending'
      }]);
      if (error) {
        console.error('❌ Error guardando aplicación:', error);
        throw error;
      }
      console.log('✅ Aplicación guardada correctamente');
      // Open WhatsApp with pre-filled message
      const message = encodeURIComponent(`Hola, acabo de aplicar a NOIZA.\nRestaurante: ${formData.restaurant_name}`);
      window.open(`https://wa.me/50688118105?text=${message}`, '_blank');
      setStep(6); // Success
    } catch (err: any) {
      console.error('❌ Error en submit:', err);
      setError('Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // --- Step Validation ---
  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.owner_name.trim().length > 0 && formData.restaurant_name.trim().length > 0;
      case 2:
        return formData.whatsapp.trim().length >= 8;
      case 3:
        return formData.business_type !== '';
      case 4:
        return formData.uses_whatsapp_orders !== null && formData.has_digital_menu !== null;
      case 5:
        return formData.main_goal.length > 0;
      default:
        return true;
    }
  };
  // --- Render Helpers ---
  const renderFloatingBackground = () => <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" data-id="element-4280">
      {/* Dark gradient base */}
      <div className="absolute inset-0 bg-[#0B0B0B]" data-id="element-4281" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,163,78,0.15)_0%,rgba(11,11,11,1)_70%)]" data-id="element-4282" />

      {/* Floating Words */}
      {BACKGROUND_WORDS.map((word, i) => <motion.div key={`word-${i}`} className="absolute text-white/5 font-black text-4xl md:text-6xl lg:text-8xl whitespace-nowrap select-none" initial={{
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      rotate: -15 + Math.random() * 30
    }} animate={{
      x: [null, Math.random() * window.innerWidth],
      y: [null, Math.random() * window.innerHeight]
    }} transition={{
      duration: 40 + Math.random() * 40,
      repeat: Infinity,
      repeatType: 'reverse',
      ease: 'linear'
    }} data-id="element-4283">
          {word}
        </motion.div>)}

      {/* Floating Foods */}
      {FLOATING_FOODS.map((food, i) => <motion.div key={`food-${i}`} className="absolute text-4xl md:text-6xl opacity-40 blur-[1px]" initial={{
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      scale: 0.5 + Math.random() * 1
    }} animate={{
      y: [null, Math.random() * window.innerHeight],
      rotate: [0, 360]
    }} transition={{
      y: {
        duration: 20 + Math.random() * 20,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut'
      },
      rotate: {
        duration: 30 + Math.random() * 30,
        repeat: Infinity,
        ease: 'linear'
      }
    }} data-id="element-4284">
          {food}
        </motion.div>)}
    </div>;
  const renderHero = () => <motion.div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 text-center" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0,
    y: -50
  }} data-id="element-4285">
      {/* Logo Area */}
      <motion.div className="mb-8 relative" initial={{
      scale: 0.8,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} data-id="element-4286">
        <div className="absolute inset-0 bg-[#C9A34E] blur-[60px] opacity-30 rounded-full" data-id="element-4287" />
        <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter relative z-10" style={{
        textShadow: '0 0 20px rgba(201,163,78,0.5)'
      }} data-id="element-4288">
          NOIZA
        </h1>
      </motion.div>

      {/* Headlines */}
      <motion.h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white max-w-4xl leading-tight mb-6" initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.2,
      duration: 0.8
    }} data-id="element-4289">
        Tu restaurante ya no necesita solo un menú.{' '}
        <span className="text-[#C9A34E]" data-id="element-4290">Necesita un sistema.</span>
      </motion.h2>

      <motion.p className="text-lg md:text-xl text-white/70 max-w-2xl mb-12" initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.4,
      duration: 0.8
    }} data-id="element-4291">
        Aplica para unirte al ecosistema digital más avanzado para restaurantes.
        Pedidos, operación, KDS y crecimiento en una sola plataforma.
      </motion.p>

      {/* CTAs */}
      <motion.div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-16" initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.6,
      duration: 0.8
    }} data-id="element-4292">
        <button onClick={handleNext} className="relative group px-8 py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-2xl font-bold text-lg overflow-hidden transition-transform hover:scale-105" data-id="element-4293">
          <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-4294" />
          <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-4295">
            Aplicar ahora <ChevronRight className="w-5 h-5" data-id="element-4296" />
          </span>
        </button>
        <button className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-2xl font-bold text-lg transition-all backdrop-blur-sm" data-id="element-4297">
          Ver cómo funciona
        </button>
      </motion.div>

      {/* Trust Pills */}
      <motion.div className="flex flex-wrap justify-center gap-3 max-w-3xl" initial="hidden" animate="visible" variants={{
      visible: {
        transition: {
          staggerChildren: 0.1,
          delayChildren: 0.8
        }
      }
    }} data-id="element-4298">
        {['Pedidos por WhatsApp', 'Tracking en tiempo real', 'KDS y operación', 'Marketplace gastronómico', 'Tecnología para restaurantes'].map((pill, i) => <motion.div key={i} variants={{
        hidden: {
          opacity: 0,
          y: 10
        },
        visible: {
          opacity: 1,
          y: 0
        }
      }} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm backdrop-blur-md" data-id="element-4299">
            {pill}
          </motion.div>)}
      </motion.div>
    </motion.div>;
  const renderFormStep = () => {
    const slideVariants = {
      enter: {
        x: 50,
        opacity: 0
      },
      center: {
        x: 0,
        opacity: 1
      },
      exit: {
        x: -50,
        opacity: 0
      }
    };
    return <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12" data-id="element-4300">
        <motion.div className="w-full max-w-xl bg-[#141414]/80 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden" initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} data-id="element-4301">
          {/* Progress Bar */}
          <div className="h-1.5 w-full bg-white/5" data-id="element-4302">
            <motion.div className="h-full bg-[#C9A34E]" initial={{
            width: 0
          }} animate={{
            width: `${step / 5 * 100}%`
          }} transition={{
            type: 'spring',
            damping: 20
          }} data-id="element-4303" />
          </div>

          <div className="p-6 md:p-10" data-id="element-4304">
            {/* Header */}
            <div className="flex items-center mb-8" data-id="element-4305">
              <button onClick={handleBack} className="p-2 -ml-2 text-white/50 hover:text-white transition-colors" data-id="element-4306">
                <ArrowLeft className="w-6 h-6" data-id="element-4307" />
              </button>
              <div className="ml-auto text-sm font-bold text-[#C9A34E]" data-id="element-4308">
                Paso {step} de 5
              </div>
            </div>

            {/* Content */}
            <AnimatePresence mode="wait" data-id="element-4309">
              <motion.div key={step} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200
            }} className="min-h-[300px]" data-id="element-4310">
                {step === 1 && <div className="space-y-6" data-id="element-4311">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-4312">
                      Empecemos con lo básico
                    </h3>
                    <div data-id="element-4313">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-4314">
                        Tu nombre completo
                      </label>
                      <input type="text" value={formData.owner_name} onChange={e => setFormData({
                    ...formData,
                    owner_name: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="Ej. Juan Pérez" autoFocus data-id="element-4315" />
                    </div>
                    <div data-id="element-4316">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-4317">
                        Nombre del restaurante
                      </label>
                      <input type="text" value={formData.restaurant_name} onChange={e => setFormData({
                    ...formData,
                    restaurant_name: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="Ej. La Pizzería" data-id="element-4318" />
                    </div>
                  </div>}

                {step === 2 && <div className="space-y-6" data-id="element-4319">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-4320">
                      ¿Cómo te contactamos?
                    </h3>
                    <div data-id="element-4321">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-4322">
                        Número de WhatsApp
                      </label>
                      <input type="tel" value={formData.whatsapp} onChange={e => setFormData({
                    ...formData,
                    whatsapp: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="88888888" autoFocus data-id="element-4323" />
                    </div>
                    <div data-id="element-4324">
                      <label className="block text-sm font-medium text-white/60 mb-2" data-id="element-4325">
                        Instagram o Facebook (Opcional)
                      </label>
                      <input type="text" value={formData.social_handle} onChange={e => setFormData({
                    ...formData,
                    social_handle: e.target.value
                  })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white text-lg focus:outline-none focus:border-[#C9A34E] focus:ring-1 focus:ring-[#C9A34E] transition-all" placeholder="@turestaurante" data-id="element-4326" />
                    </div>
                  </div>}

                {step === 3 && <div className="space-y-6" data-id="element-4327">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-4328">
                      ¿Qué tipo de negocio tienes?
                    </h3>
                    <div className="grid grid-cols-2 gap-3" data-id="element-4329">
                      {(['Restaurante', 'Cafetería', 'Pizzería', 'Comida rápida', 'Panadería', 'Otro'] as BusinessType[]).map(type => <button key={type} onClick={() => setFormData({
                    ...formData,
                    business_type: type
                  })} className={`p-4 rounded-xl border text-left transition-all ${formData.business_type === type ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-4330">
                          <span className="font-medium" data-id="element-4331">{type}</span>
                        </button>)}
                    </div>
                  </div>}

                {step === 4 && <div className="space-y-8" data-id="element-4332">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-4333">
                      Operación actual
                    </h3>

                    <div data-id="element-4334">
                      <label className="block text-sm font-medium text-white/60 mb-3" data-id="element-4335">
                        ¿Ya reciben pedidos por WhatsApp?
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-4336">
                        <button onClick={() => setFormData({
                      ...formData,
                      uses_whatsapp_orders: true
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.uses_whatsapp_orders === true ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-4337">
                          Sí
                        </button>
                        <button onClick={() => setFormData({
                      ...formData,
                      uses_whatsapp_orders: false
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.uses_whatsapp_orders === false ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-4338">
                          No
                        </button>
                      </div>
                    </div>

                    <div data-id="element-4339">
                      <label className="block text-sm font-medium text-white/60 mb-3" data-id="element-4340">
                        ¿Tienen menú digital hoy?
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-4341">
                        <button onClick={() => setFormData({
                      ...formData,
                      has_digital_menu: true
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.has_digital_menu === true ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-4342">
                          Sí
                        </button>
                        <button onClick={() => setFormData({
                      ...formData,
                      has_digital_menu: false
                    })} className={`p-4 rounded-xl border text-center transition-all ${formData.has_digital_menu === false ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-4343">
                          No
                        </button>
                      </div>
                    </div>
                  </div>}

                {step === 5 && <div className="space-y-6" data-id="element-4344">
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-4345">
                      ¿Qué les gustaría mejorar primero?
                    </h3>
                    <p className="text-white/50 text-sm mb-6" data-id="element-4346">
                      Puedes seleccionar varias opciones
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-4347">
                      {(['Más pedidos', 'Mejor control de cocina', 'Menú digital', 'Delivery / express', 'Orden y operación', 'Todo lo anterior'] as MainGoal[]).map(goal => {
                    const isSelected = formData.main_goal.includes(goal);
                    return <button key={goal} onClick={() => {
                      if (goal === 'Todo lo anterior') {
                        setFormData({
                          ...formData,
                          main_goal: ['Todo lo anterior']
                        });
                      } else {
                        const newGoals = isSelected ? formData.main_goal.filter(g => g !== goal) : [...formData.main_goal.filter(g => g !== 'Todo lo anterior'), goal];
                        setFormData({
                          ...formData,
                          main_goal: newGoals
                        });
                      }
                    }} className={`p-4 rounded-xl border text-left transition-all ${isSelected ? 'bg-[#C9A34E]/10 border-[#C9A34E] text-[#C9A34E]' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-4348">
                            <span className="font-medium text-sm" data-id="element-4349">{goal}</span>
                          </button>;
                  })}
                    </div>
                  </div>}
              </motion.div>
            </AnimatePresence>

            {/* Footer Actions */}
            <div className="mt-10 pt-6 border-t border-white/10" data-id="element-4350">
              {error && <p className="text-red-400 text-sm mb-4 text-center" data-id="element-4351">{error}</p>}

              {step < 5 ? <button onClick={handleNext} disabled={!isStepValid()} className="w-full py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#d4b365]" data-id="element-4352">
                  Continuar
                </button> : <button onClick={handleSubmit} disabled={!isStepValid() || isSubmitting} className="w-full py-4 bg-[#C9A34E] text-[#0B0B0B] rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#d4b365] flex items-center justify-center gap-2" data-id="element-4353">
                  {isSubmitting ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-4354" /> Enviando...
                    </> : 'Enviar solicitud'}
                </button>}
            </div>
          </div>
        </motion.div>
      </div>;
  };
  const renderSuccess = () => <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 text-center" data-id="element-4355">
      <motion.div className="w-full max-w-md bg-[#141414]/80 backdrop-blur-2xl border border-white/10 rounded-3xl p-10 shadow-2xl" initial={{
      scale: 0.9,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} data-id="element-4356">
        <motion.div className="w-20 h-20 bg-[#C9A34E]/20 rounded-full flex items-center justify-center mx-auto mb-6" initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        delay: 0.2,
        type: 'spring'
      }} data-id="element-4357">
          <CheckCircle2 className="w-10 h-10 text-[#C9A34E]" data-id="element-4358" />
        </motion.div>

        <h2 className="text-3xl font-bold text-white mb-4" data-id="element-4359">
          Recibimos tu solicitud
        </h2>
        <p className="text-white/70 mb-8 leading-relaxed" data-id="element-4360">
          Si tu restaurante encaja con el ecosistema NOIZA, nuestro equipo se
          pondrá en contacto contigo muy pronto.
        </p>

        <a href="https://wa.me/50688888888" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full py-4 bg-[#25D366] text-white rounded-xl font-bold text-lg hover:bg-[#20bd5a] transition-colors" data-id="element-4361">
          <MessageCircle className="w-5 h-5" data-id="element-4362" />
          Hablar con NOIZA
        </a>
      </motion.div>
    </div>;
  return <div className="min-h-screen bg-[#0B0B0B] font-sans selection:bg-[#C9A34E]/30 text-white" data-id="element-4363">
      {renderFloatingBackground()}

      <AnimatePresence mode="wait" data-id="element-4364">
        {step === 0 && <motion.div key="hero" className="w-full" data-id="element-4365">
            {renderHero()}
          </motion.div>}
        {step > 0 && step < 6 && <motion.div key="form" className="w-full" data-id="element-4366">
            {renderFormStep()}
          </motion.div>}
        {step === 6 && <motion.div key="success" className="w-full" data-id="element-4367">
            {renderSuccess()}
          </motion.div>}
      </AnimatePresence>
    </div>;
}